import { useEffect } from 'react';

import { useSetLeaseDetailsSubmitLock } from '@/modules/application-tracker/components/lease-form';

import {
  useInviteEligibilityCheck,
  type InviteEligibilityRowFilter,
} from '../hooks/use-invite-eligibility-check';
import {
  inviteEligibilityFlow,
  useTrackInviteEligibilityPreflight,
} from '../hooks/use-invite-eligibility-tracking';

type InviteEligibilityControllerProps = {
  /**
   * Defaults to every client-valid email (create invite).
   * Edit invite passes `includeEditInviteEligibilityRow` so unchanged recipients stay out.
   * Pass a stable function — an inline function resets the debounce on every render.
   */
  includeRow?: InviteEligibilityRowFilter;
  /** Edit invite passes the invite id so the invite does not match its own rows. */
  inviteAlphaId?: string;
};

/**
 * Mounts once inside the invite form (FormProvider) to own the eligibility
 * query side effects — syncing block outcomes onto email fields as RHF errors,
 * and preflight telemetry.
 * Banners read the same React Query cache via {@link useInviteEligibilityCheck}.
 */
export function InviteEligibilityController({
  includeRow,
  inviteAlphaId,
}: InviteEligibilityControllerProps) {
  const { hasBlock, blockCount, preflight } = useInviteEligibilityCheck({
    syncFieldErrors: true,
    includeRow,
    inviteAlphaId,
  });
  const setSubmitLocked = useSetLeaseDetailsSubmitLock();
  useTrackInviteEligibilityPreflight({
    flow: inviteEligibilityFlow(inviteAlphaId),
    preflight,
    blockCount,
  });

  useEffect(() => {
    setSubmitLocked(hasBlock);
    return () => setSubmitLocked(false);
  }, [hasBlock, setSubmitLocked]);

  return null;
}
